import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '@/lib/api';
import { formatDateNL } from '@/lib/format';
import { useToast } from '@/hooks/use-toast';
import {
  FileText, Upload, Trash2, Download, Loader2, Pencil, X, Save,
  AlertTriangle, AlertOctagon, CalendarClock, PenLine, Send, CheckCircle2, RotateCcw,
} from 'lucide-react';

/* ----------------------------- Constants ------------------------------ */

export const DOC_CATEGORIES = [
  { value: 'id_bewijs', label: 'ID-bewijs', expiryRequired: true },
  { value: 'vog', label: 'VOG', expiryRequired: true },
  { value: 'rijbewijs', label: 'Rijbewijs', expiryRequired: true },
  { value: 'contract', label: 'Contract / Addendum', expiryRequired: false },
  { value: 'diploma', label: 'Diploma / Certificaat', expiryRequired: false },
  { value: 'overig', label: 'Overig', expiryRequired: false },
];

export const CATEGORY_LABEL = Object.fromEntries(
  DOC_CATEGORIES.map((c) => [c.value, c.label]),
);

const ALLOWED_EXT = ['pdf', 'jpg', 'jpeg', 'png', 'docx'];
const MAX_BYTES = 10 * 1024 * 1024;

/* ----------------------------- Badges --------------------------------- */

export function ExpiryBadge({ status, days, expiresAt }) {
  if (!status || status === 'none' || status === 'ok') {
    if (expiresAt) {
      return (
        <span
          data-testid="doc-expiry-ok"
          className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 border border-emerald-200"
        >
          <CalendarClock size={11} /> Geldig t/m {formatDateNL(expiresAt)}
        </span>
      );
    }
    return (
      <span
        data-testid="doc-expiry-none"
        className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600 border border-slate-200"
      >
        Geen vervaldatum
      </span>
    );
  }
  if (status === 'expired') {
    return (
      <span
        data-testid="doc-expiry-expired"
        className="inline-flex items-center gap-1 rounded-md bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-800 border border-rose-300"
      >
        <AlertOctagon size={11} /> Verlopen op {formatDateNL(expiresAt)}
      </span>
    );
  }
  if (status === 'red') {
    return (
      <span
        data-testid="doc-expiry-red"
        className="inline-flex items-center gap-1 rounded-md bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-700 border border-rose-200"
      >
        <AlertOctagon size={11} /> Nog {days}d (t/m {formatDateNL(expiresAt)})
      </span>
    );
  }
  if (status === 'orange') {
    return (
      <span
        data-testid="doc-expiry-orange"
        className="inline-flex items-center gap-1 rounded-md bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800 border border-amber-200"
      >
        <AlertTriangle size={11} /> Nog {days}d (t/m {formatDateNL(expiresAt)})
      </span>
    );
  }
  return null;
}

function FileSize({ bytes }) {
  if (!bytes) return null;
  const kb = bytes / 1024;
  if (kb < 1024) return <span>{kb.toFixed(0)} KB</span>;
  return <span>{(kb / 1024).toFixed(1)} MB</span>;
}

export const SIGN_ACK_TEXT = 'Ik heb dit document gelezen en ga akkoord';

export function SignBadge({ doc }) {
  const st = doc.sign_status || 'niet_vrijgegeven';
  if (st === 'getekend') {
    return (
      <span data-testid={`doc-sign-getekend-${doc.id}`} className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 border border-emerald-200">
        <CheckCircle2 size={11} /> Getekend{doc.signed_at ? ` op ${formatDateNL(doc.signed_at)}` : ''}{doc.signed_name ? ` · ${doc.signed_name}` : ''}
      </span>
    );
  }
  if (st === 'te_tekenen') {
    return (
      <span data-testid={`doc-sign-tetekenen-${doc.id}`} className="inline-flex items-center gap-1 rounded-md bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800 border border-amber-200">
        <PenLine size={11} /> Te tekenen
      </span>
    );
  }
  return (
    <span data-testid={`doc-sign-niet-${doc.id}`} className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 text-[11px] text-slate-500 border border-slate-200">
      Niet vrijgegeven
    </span>
  );
}

function SignDialog({ doc, onClose, onSigned, downloadUrl }) {
  const [fullName, setFullName] = useState('');
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const { toast } = useToast();

  if (!doc) return null;

  const submit = async (e) => {
    e?.preventDefault();
    if (!fullName.trim()) { setError('Vul je volledige naam in'); return; }
    if (!ack) { setError('Bevestig dat je het document gelezen hebt en akkoord gaat'); return; }
    setBusy(true);
    setError('');
    try {
      const { data } = await api.post(`/portal/my-documents/${doc.id}/sign`, {
        full_name: fullName.trim(), acknowledgment: true,
      });
      onSigned?.(data);
      toast({ title: 'Document ondertekend', description: doc.title });
      onClose?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Ondertekenen mislukt');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell open={!!doc} onClose={onClose} title="Document ondertekenen" icon={PenLine} testid="doc-sign-modal">
      <form onSubmit={submit} className="space-y-3">
        <p className="text-xs text-slate-500">{doc.category_label} · {doc.filename}</p>
        <button
          type="button"
          onClick={async () => { await downloadDocument(doc, { downloadUrl }); }}
          data-testid="doc-sign-open"
          className="flex w-full items-center justify-center gap-1.5 rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
        >
          <Download size={14} /> Document openen / lezen
        </button>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Volledige naam</label>
          <input
            value={fullName} onChange={(e) => setFullName(e.target.value)}
            data-testid="doc-sign-name" placeholder="bv. Jan Jansen"
            className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm"
          />
        </div>
        <label className="flex items-start gap-2 text-sm text-slate-700">
          <input
            type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)}
            data-testid="doc-sign-ack" className="mt-0.5"
          />
          <span>{SIGN_ACK_TEXT}.</span>
        </label>
        {error && <p className="rounded-md bg-rose-50 px-2 py-1.5 text-xs text-rose-700" data-testid="doc-sign-error">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50">Annuleren</button>
          <button
            type="submit" disabled={busy || !ack || !fullName.trim()}
            data-testid="doc-sign-submit"
            className="flex items-center gap-1.5 rounded-md bg-brand-navy px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-navy-light disabled:opacity-60"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Ondertekenen
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

/* ------------------------ Modal shell --------------------------------- */

function ModalShell({ open, onClose, title, icon: Icon, testid, children }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    // Prevent body scroll when modal open
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
      data-testid={testid}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-md rounded-xl bg-white p-5 shadow-2xl"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            {Icon && <Icon size={16} />} {title}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 hover:bg-slate-100 z-10"
            data-testid={`${testid}-close`}
            aria-label="Sluiten"
          >
            <X size={16} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/* ------------------------ Upload form (modal-like) -------------------- */

function UploadDialog({ open, onClose, employeeId, onUploaded }) {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [category, setCategory] = useState('id_bewijs');
  const [title, setTitle] = useState('');
  const [issuedAt, setIssuedAt] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const expiryRequired = useMemo(
    () => DOC_CATEGORIES.find((c) => c.value === category)?.expiryRequired,
    [category],
  );

  useEffect(() => {
    if (!open) {
      setFile(null);
      setTitle('');
      setIssuedAt('');
      setExpiresAt('');
      setNotes('');
      setError('');
      setCategory('id_bewijs');
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [open]);

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setError('');
    const ext = f.name.split('.').pop()?.toLowerCase();
    if (!ALLOWED_EXT.includes(ext)) {
      setError(`Bestandstype niet toegestaan. Alleen ${ALLOWED_EXT.join(', ').toUpperCase()}.`);
      setFile(null);
      return;
    }
    if (f.size > MAX_BYTES) {
      setError(`Bestand te groot. Max 10 MB (${(f.size / 1024 / 1024).toFixed(1)} MB).`);
      setFile(null);
      return;
    }
    setFile(f);
  };

  const submit = async (e) => {
    e?.preventDefault();
    setError('');
    if (!file) { setError('Selecteer een bestand'); return; }
    if (expiryRequired && !expiresAt) {
      setError(`Vervaldatum verplicht voor ${CATEGORY_LABEL[category]}`);
      return;
    }
    setBusy(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('employee_id', employeeId);
    fd.append('category', category);
    if (title) fd.append('title', title);
    if (issuedAt) fd.append('issued_at', issuedAt);
    if (expiresAt) fd.append('expires_at', expiresAt);
    if (notes) fd.append('notes', notes);
    try {
      const { data } = await api.post('/documents/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onUploaded?.(data);
      onClose?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Upload mislukt');
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  return (
    <ModalShell open={open} onClose={onClose} title="Document uploaden" icon={Upload} testid="doc-upload-modal">
      <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Categorie</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              data-testid="doc-upload-category"
              className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm"
            >
              {DOC_CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Titel (optioneel)
            </label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              data-testid="doc-upload-title"
              placeholder={CATEGORY_LABEL[category]}
              className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Afgegeven op</label>
              <input
                type="date" value={issuedAt}
                onChange={(e) => setIssuedAt(e.target.value)}
                data-testid="doc-upload-issued"
                className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Vervaldatum {expiryRequired && <span className="text-rose-600">*</span>}
              </label>
              <input
                type="date" value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                data-testid="doc-upload-expires"
                className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Bestand (PDF/JPG/PNG/DOCX, max 10 MB)</label>
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.docx"
              onChange={onPickFile}
              ref={fileInputRef}
              data-testid="doc-upload-file"
              className="block w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm file:mr-3 file:rounded file:border-0 file:bg-brand-navy file:px-3 file:py-1 file:text-xs file:font-medium file:text-white"
            />
            {file && (
              <p className="mt-1 text-[11px] text-slate-500">
                {file.name} · <FileSize bytes={file.size} />
              </p>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Notities (optioneel)</label>
            <textarea
              rows={2} value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={1000}
              data-testid="doc-upload-notes"
              className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm"
            />
          </div>
          {error && (
            <p className="rounded-md bg-rose-50 px-2 py-1.5 text-xs text-rose-700" data-testid="doc-upload-error">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button" onClick={onClose}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50"
            >
              Annuleren
            </button>
            <button
              type="submit"
              disabled={busy}
              data-testid="doc-upload-submit"
              className="flex items-center gap-1.5 rounded-md bg-brand-navy px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-navy-light disabled:opacity-60"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              Uploaden
            </button>
          </div>
        </form>
    </ModalShell>
  );
}

/* --------------------------- Edit metadata dialog --------------------- */

function EditDialog({ doc, onClose, onSaved }) {
  const [title, setTitle] = useState(doc?.title || '');
  const [issuedAt, setIssuedAt] = useState(doc?.issued_at || '');
  const [expiresAt, setExpiresAt] = useState(doc?.expires_at || '');
  const [notes, setNotes] = useState(doc?.notes || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (!doc) return null;

  const save = async (e) => {
    e?.preventDefault();
    setBusy(true);
    setError('');
    try {
      const payload = {
        title: title || null,
        issued_at: issuedAt || null,
        expires_at: expiresAt || null,
        notes: notes || null,
      };
      const { data } = await api.put(`/documents/${doc.id}`, payload);
      onSaved?.(data);
      onClose?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Bijwerken mislukt');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell open={!!doc} onClose={onClose} title="Document bewerken" icon={Pencil} testid="doc-edit-modal">
        <form onSubmit={save} className="space-y-3">
          <p className="text-xs text-slate-500">
            {CATEGORY_LABEL[doc.category]} · {doc.filename}
          </p>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Titel</label>
            <input
              value={title} onChange={(e) => setTitle(e.target.value)}
              data-testid="doc-edit-title"
              className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Afgegeven op</label>
              <input
                type="date" value={issuedAt}
                onChange={(e) => setIssuedAt(e.target.value)}
                className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Vervaldatum</label>
              <input
                type="date" value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                data-testid="doc-edit-expires"
                className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Notities</label>
            <textarea
              rows={2} value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm"
            />
          </div>
          {error && (
            <p className="rounded-md bg-rose-50 px-2 py-1.5 text-xs text-rose-700">{error}</p>
          )}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50">
              Annuleren
            </button>
            <button type="submit" disabled={busy}
              data-testid="doc-edit-submit"
              className="flex items-center gap-1.5 rounded-md bg-brand-navy px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-navy-light disabled:opacity-60"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Opslaan
            </button>
          </div>
        </form>
    </ModalShell>
  );
}

/* ------------------------ Download helper ----------------------------- */

async function downloadDocument(doc, options = {}) {
  const { downloadUrl, toast } = options;
  const url = downloadUrl || `/documents/${doc.id}/download`;
  try {
    const resp = await api.get(url, { responseType: 'blob' });
    const blob = new Blob([resp.data], { type: doc.mime_type });
    const objectUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = doc.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
  } catch (e) {
    if (toast) {
      toast({ title: 'Download mislukt', variant: 'destructive' });
    }
  }
}

/* ------------------------ Main: DocumentenSection --------------------- */

export default function DocumentenSection({
  employeeId,
  canEdit = true,
  isPortal = false,
  listUrl,           // optional override (portal uses /portal/my-documents)
  downloadUrlBuilder, // optional fn(docId) → url
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [signingDoc, setSigningDoc] = useState(null);
  const [error, setError] = useState('');
  const { toast } = useToast();

  const effectiveListUrl = listUrl || '/documents';
  const buildDownloadUrl = downloadUrlBuilder || ((id) => `/documents/${id}/download`);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = listUrl ? {} : { employee_id: employeeId };
      const { data } = await api.get(effectiveListUrl, { params });
      setItems(data.items || []);
    } catch (e) {
      setError('Kon documenten niet laden');
    } finally {
      setLoading(false);
    }
  }, [employeeId, effectiveListUrl, listUrl]);

  useEffect(() => {
    if (employeeId || listUrl) load();
  }, [employeeId, listUrl, load]);

  const onDelete = async (doc) => {
    if (!window.confirm(`"${doc.title}" verwijderen? Dit kan niet ongedaan worden.`)) return;
    try {
      await api.delete(`/documents/${doc.id}`);
      setItems((s) => s.filter((d) => d.id !== doc.id));
      toast({ title: 'Document verwijderd' });
    } catch (e) {
      toast({ title: 'Verwijderen mislukt', variant: 'destructive' });
    }
  };

  const onDownload = async (doc) => {
    await downloadDocument(doc, { downloadUrl: buildDownloadUrl(doc.id), toast });
  };

  const onRelease = async (doc) => {
    try {
      const { data } = await api.post(`/documents/${doc.id}/release`);
      setItems((s) => s.map((d) => (d.id === doc.id ? data : d)));
      toast({ title: 'Vrijgegeven ter ondertekening', description: doc.title });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast({ title: 'Vrijgeven mislukt', description: typeof detail === 'string' ? detail : undefined, variant: 'destructive' });
    }
  };

  const onUnrelease = async (doc) => {
    try {
      const { data } = await api.post(`/documents/${doc.id}/unrelease`);
      setItems((s) => s.map((d) => (d.id === doc.id ? data : d)));
      toast({ title: 'Vrijgave ingetrokken' });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast({ title: 'Intrekken mislukt', description: typeof detail === 'string' ? detail : undefined, variant: 'destructive' });
    }
  };

  // Group counts
  const counts = useMemo(() => {
    const c = { expired: 0, red: 0, orange: 0 };
    items.forEach((d) => {
      if (d.expiry_status in c) c[d.expiry_status] += 1;
    });
    return c;
  }, [items]);

  return (
    <div className="space-y-3" data-testid="documenten-section">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <FileText size={18} /> Documenten
            <span className="ml-1 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">
              {items.length}
            </span>
          </h3>
          {counts.expired + counts.red + counts.orange > 0 && (
            <div className="flex items-center gap-1.5">
              {counts.expired > 0 && (
                <span className="rounded-md bg-rose-100 px-2 py-0.5 text-[11px] font-medium text-rose-800">
                  {counts.expired} verlopen
                </span>
              )}
              {counts.red > 0 && (
                <span className="rounded-md bg-rose-50 px-2 py-0.5 text-[11px] font-medium text-rose-700 border border-rose-200">
                  {counts.red} &lt; 7d
                </span>
              )}
              {counts.orange > 0 && (
                <span className="rounded-md bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800 border border-amber-200">
                  {counts.orange} &lt; 30d
                </span>
              )}
            </div>
          )}
        </div>
        {canEdit && (
          <button
            onClick={() => setShowUpload(true)}
            data-testid="btn-upload-document"
            className="flex items-center gap-1.5 rounded-md bg-brand-navy px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-navy-light"
          >
            <Upload size={14} /> Uploaden
          </button>
        )}
      </div>

      {error && (
        <div className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-6 text-sm text-slate-500 flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Laden…
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500" data-testid="documenten-empty">
            <FileText size={32} className="mx-auto mb-2 text-slate-300" />
            <p>Nog geen documenten geüpload.</p>
            {canEdit && (
              <button
                onClick={() => setShowUpload(true)}
                className="mt-3 inline-flex items-center gap-1 rounded-md border border-slate-300 px-3 py-1 text-xs hover:bg-slate-50"
              >
                <Upload size={12} /> Eerste document toevoegen
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                  <th className="py-2 px-3">Categorie</th>
                  <th className="py-2 px-3">Titel / Bestand</th>
                  <th className="py-2 px-3">Vervaldatum</th>
                  <th className="py-2 px-3">Ondertekening</th>
                  <th className="py-2 px-3">Geüpload</th>
                  <th className="py-2 px-3 text-right">Acties</th>
                </tr>
              </thead>
              <tbody>
                {items.map((d) => (
                  <tr
                    key={d.id}
                    className="border-b border-slate-100 hover:bg-slate-50/50"
                    data-testid={`doc-row-${d.id}`}
                  >
                    <td className="py-2 px-3">
                      <span className="rounded bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700">
                        {d.category_label}
                      </span>
                    </td>
                    <td className="py-2 px-3">
                      <div className="font-medium text-slate-900">{d.title}</div>
                      <div className="text-[11px] text-slate-500">
                        {d.filename} · <FileSize bytes={d.size_bytes} />
                      </div>
                    </td>
                    <td className="py-2 px-3">
                      <ExpiryBadge
                        status={d.expiry_status}
                        days={d.days_until_expiry}
                        expiresAt={d.expires_at}
                      />
                    </td>
                    <td className="py-2 px-3">
                      <SignBadge doc={d} />
                    </td>
                    <td className="py-2 px-3 text-[11px] text-slate-600">
                      {formatDateNL(d.uploaded_at)}
                      {d.uploaded_by_name && <div className="text-slate-400">door {d.uploaded_by_name}</div>}
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => onDownload(d)}
                          data-testid={`doc-download-${d.id}`}
                          title="Download"
                          className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-brand-navy"
                        >
                          <Download size={14} />
                        </button>
                        {isPortal && d.sign_status === 'te_tekenen' && (
                          <button
                            onClick={() => setSigningDoc(d)}
                            data-testid={`doc-sign-${d.id}`}
                            className="flex items-center gap-1 rounded-md bg-brand-navy px-2.5 py-1 text-xs font-medium text-white hover:bg-brand-navy-light"
                          >
                            <PenLine size={12} /> Tekenen
                          </button>
                        )}
                        {canEdit && d.sign_status !== 'getekend' && (
                          d.sign_status === 'te_tekenen' ? (
                            <button
                              onClick={() => onUnrelease(d)}
                              data-testid={`doc-unrelease-${d.id}`}
                              title="Vrijgave intrekken"
                              className="rounded-md p-1.5 text-slate-500 hover:bg-amber-50 hover:text-amber-700"
                            >
                              <RotateCcw size={14} />
                            </button>
                          ) : (
                            <button
                              onClick={() => onRelease(d)}
                              data-testid={`doc-release-${d.id}`}
                              title="Vrijgeven ter ondertekening"
                              className="flex items-center gap-1 rounded-md border border-brand-navy/30 px-2 py-1 text-xs font-medium text-brand-navy hover:bg-brand-amber/10"
                            >
                              <PenLine size={12} /> Vrijgeven
                            </button>
                          )
                        )}
                        {canEdit && (
                          <>
                            <button
                              onClick={() => setEditingDoc(d)}
                              data-testid={`doc-edit-${d.id}`}
                              title="Bewerken"
                              className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-brand-navy"
                            >
                              <Pencil size={14} />
                            </button>
                            <button
                              onClick={() => onDelete(d)}
                              data-testid={`doc-delete-${d.id}`}
                              title="Verwijderen"
                              className="rounded-md p-1.5 text-slate-500 hover:bg-rose-50 hover:text-rose-600"
                            >
                              <Trash2 size={14} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <UploadDialog
        open={showUpload}
        onClose={() => setShowUpload(false)}
        employeeId={employeeId}
        onUploaded={(doc) => {
          setItems((s) => [doc, ...s]);
          toast({ title: 'Document geüpload', description: doc.title });
        }}
      />
      <EditDialog
        doc={editingDoc}
        onClose={() => setEditingDoc(null)}
        onSaved={(updated) => {
          setItems((s) => s.map((d) => (d.id === updated.id ? updated : d)));
          setEditingDoc(null);
          toast({ title: 'Document bijgewerkt' });
        }}
      />
      <SignDialog
        doc={signingDoc}
        downloadUrl={signingDoc ? buildDownloadUrl(signingDoc.id) : undefined}
        onClose={() => setSigningDoc(null)}
        onSigned={(updated) => {
          setItems((s) => s.map((d) => (d.id === updated.id ? { ...d, ...updated } : d)));
          setSigningDoc(null);
        }}
      />
    </div>
  );
}
